import React, { useState, useEffect, useCallback } from 'react';
import { Play, Eye, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { getSchoolInfo, schoolHeader } from '../../utils/printHelper';

const MONTHS = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function SalarySlips() {
  const { currentUser } = useAuth();
  const isTeacher = currentUser?.role === 'teacher';
  const myStaffId = currentUser?.profileId || '';

  const now = new Date();
  const [slips,        setSlips]        = useState([]);
  const [staff,        setStaff]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [generating,   setGenerating]   = useState(false);
  const [expanded,     setExpanded]     = useState(null);
  const [filterStaff,  setFilterStaff]  = useState(isTeacher ? myStaffId : '');
  const [filterMonth,  setFilterMonth]  = useState('');
  const [filterYear,   setFilterYear]   = useState(String(now.getFullYear()));

  // Generate dialog state
  const [showGen,  setShowGen]  = useState(false);
  const [genMonth, setGenMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [genYear,  setGenYear]  = useState(now.getMonth() === 0 ? now.getFullYear()-1 : now.getFullYear());
  const [workDays, setWorkDays] = useState(26);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStaff) params.staffId = filterStaff;
      if (filterMonth) params.month   = filterMonth;
      if (filterYear)  params.year    = filterYear;
      const [sl, st] = await Promise.all([api.getSalarySlips(params), api.list('staff')]);
      setSlips(sl);
      setStaff(st.filter(s => s.status === 'active'));
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  }, [filterStaff, filterMonth, filterYear]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.generateSalarySlips({ month: genMonth, year: genYear, workingDays: workDays });
      alert(`✅ Generated ${res.count} salary slip(s) for ${MONTHS[genMonth]} ${genYear}`);
      setShowGen(false);
      await load();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setGenerating(false); }
  };

  const printSlip = async (slip) => {
    const school = await getSchoolInfo();
    const w = window.open('', '_blank');
    w.document.write(slipHTML(slip, school));
    w.document.close();
    w.print();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{isTeacher ? 'My Pay Slips' : 'Salary Slips'}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{isTeacher ? 'Your monthly salary slips' : 'Generate and view monthly payslips with LOP calculations'}</p>
        </div>
        {!isTeacher && (
          <button onClick={() => setShowGen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
            <Play size={14} /> Generate Payroll
          </button>
        )}
      </div>

      {/* Generate dialog */}
      {showGen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Generate Monthly Payroll</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Month</label>
                  <select value={genMonth} onChange={e => setGenMonth(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                    {MONTHS.slice(1).map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Year</label>
                  <select value={genYear} onChange={e => setGenYear(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                    {[now.getFullYear()-1, now.getFullYear()].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Working Days in Month</label>
                <input type="number" min="1" max="31" value={workDays}
                  onChange={e => setWorkDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
              </div>
              <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
                Generates slips for all active staff with salary structures. LOP is calculated
                from absences + late-arrival half-day deductions per your attendance policy.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowGen(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={generate} disabled={generating}
                className="px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
                {generating ? 'Generating…' : `Generate ${MONTHS[genMonth]} ${genYear}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        {!isTeacher && (
          <select value={filterStaff} onChange={e => setFilterStaff(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 min-w-48">
            <option value="">All Staff</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
          </select>
        )}
        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          <option value="">All Months</option>
          {MONTHS.slice(1).map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          {[now.getFullYear()-1, now.getFullYear()].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Summary */}
      {!loading && slips.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-5">
          <Stat label="Slips" value={slips.length} color="purple" />
          <Stat label="Total Gross" value={'₹' + Math.round(slips.reduce((s,r) => s+Number(r.grossSalary||0),0)).toLocaleString('en-IN')} color="blue" />
          <Stat label="Total Net" value={'₹' + Math.round(slips.reduce((s,r) => s+Number(r.netSalary||0),0)).toLocaleString('en-IN')} color="green" />
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : slips.length === 0 ? (
        <div className="text-center py-16 text-gray-400">No salary slips found. Generate payroll first.</div>
      ) : (
        <div className="space-y-3">
          {slips.map(slip => (
            <div key={slip.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 cursor-pointer"
                onClick={() => setExpanded(expanded === slip.id ? null : slip.id)}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-bold">
                    {slip.firstName?.[0]}{slip.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{slip.firstName} {slip.lastName}</p>
                    <p className="text-xs text-gray-400">{slip.designation} · {MONTHS[slip.month]} {slip.year}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-gray-400">Gross</p>
                    <p className="text-sm font-medium text-gray-700">₹{Number(slip.grossSalary||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  {Number(slip.lopDays) > 0 && (
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-gray-400">LOP Days</p>
                      <p className="text-sm font-semibold text-red-500">{slip.lopDays}</p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Net Pay</p>
                    <p className="text-base font-bold text-green-600">₹{Number(slip.netSalary||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={e => { e.stopPropagation(); printSlip(slip); }}
                      className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Print">
                      <Download size={15} />
                    </button>
                    {expanded === slip.id ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </div>
              </div>

              {expanded === slip.id && <SlipDetail slip={slip} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  const colors = { purple:'bg-purple-50 text-purple-700', blue:'bg-blue-50 text-blue-700', green:'bg-green-50 text-green-700' };
  return (
    <div className={`rounded-xl border border-gray-100 px-5 py-4 ${colors[color].split(' ')[0]}`}>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`text-xl font-bold mt-1 ${colors[color].split(' ')[1]}`}>{value}</p>
    </div>
  );
}

function SlipDetail({ slip }) {
  const rows = [
    ['Working Days', slip.workingDays], ['Present', slip.presentDays],
    ['Leave', slip.leaveDays], ['LOP Days', slip.lopDays],
  ];
  const earnings = [
    ['Basic Salary', slip.basic], ['HRA', slip.hra], ['DA', slip.da],
    ['Transport Allow.', slip.transportAllowance], ['Other Allow.', slip.otherAllowance],
  ].filter(([,v]) => Number(v) > 0);
  const deductions = [
    ['LOP Deduction', slip.lopDeduction], ['PF', slip.pfDeduction],
    ['ESI', slip.esiDeduction], ['Professional Tax', slip.professionalTax],
  ].filter(([,v]) => Number(v) > 0);

  return (
    <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
      {/* Attendance */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {rows.map(([l,v]) => (
          <div key={l} className="bg-white rounded-lg border border-gray-100 px-3 py-2 text-center">
            <p className="text-xs text-gray-400">{l}</p>
            <p className={`text-lg font-bold ${l==='LOP Days'&&Number(v)>0?'text-red-500':'text-gray-700'}`}>{v}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Earnings</p>
          {earnings.map(([l,v]) => (
            <div key={l} className="flex justify-between py-1 border-b border-gray-50 last:border-0">
              <span className="text-sm text-gray-600">{l}</span>
              <span className="text-sm font-medium text-gray-800">₹{Math.round(v).toLocaleString('en-IN')}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 mt-1 border-t border-gray-200">
            <span className="text-sm font-semibold text-gray-700">Gross</span>
            <span className="text-sm font-bold text-gray-800">₹{Number(slip.grossSalary||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Deductions</p>
          {deductions.map(([l,v]) => (
            <div key={l} className="flex justify-between py-1 border-b border-gray-50 last:border-0">
              <span className="text-sm text-gray-600">{l}</span>
              <span className="text-sm font-medium text-red-500">-₹{Math.round(v).toLocaleString('en-IN')}</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 mt-1 border-t border-gray-200">
            <span className="text-sm font-semibold text-gray-700">NET SALARY</span>
            <span className="text-base font-bold text-green-600">₹{Number(slip.netSalary||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function slipHTML(slip, school = {}) {
  return `<!DOCTYPE html><html><head><title>Salary Slip — ${slip.firstName} ${slip.lastName}</title>
  <style>body{font-family:Arial,sans-serif;padding:30px;max-width:700px;margin:0 auto;font-size:15px}
  table{width:100%;border-collapse:collapse;margin-top:12px}
  td,th{padding:6px 10px;border:1px solid #e5e7eb;font-size:15px}
  th{background:#f3e8ff;font-weight:600;text-align:left}
  .net{background:#f0fdf4;font-size:16px;font-weight:bold;color:#16a34a}
  @page{margin:3mm}@media print{.no-print{display:none}body{padding:4px}*{color:#000!important}}</style></head><body>
  ${schoolHeader(school, '#6b21a8', `Salary Slip &mdash; ${MONTHS[slip.month]} ${slip.year}`)}
  <table><tr><td><b>Name:</b> ${slip.firstName} ${slip.lastName}</td>
  <td><b>Designation:</b> ${slip.designation}</td></tr>
  <tr><td><b>Employee ID:</b> ${slip.employeeId||'—'}</td>
  <td><b>Month:</b> ${MONTHS[slip.month]} ${slip.year}</td></tr></table>
  <table style="margin-top:16px"><thead><tr><th>Earnings</th><th>Amount (₹)</th>
  <th>Deductions</th><th>Amount (₹)</th></tr></thead><tbody>
  <tr><td>Basic Salary</td><td>₹${Number(slip.basic||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td>LOP (${slip.lopDays} days)</td><td>₹${Number(slip.lopDeduction||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
  <tr><td>HRA</td><td>₹${Number(slip.hra||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td>PF</td><td>₹${Number(slip.pfDeduction||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
  <tr><td>DA</td><td>₹${Number(slip.da||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td>ESI</td><td>₹${Number(slip.esiDeduction||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
  <tr><td>Transport Allow.</td><td>₹${Number(slip.transportAllowance||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td>Professional Tax</td><td>₹${Number(slip.professionalTax||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
  <tr><td>Other Allow.</td><td>₹${Number(slip.otherAllowance||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td></td><td></td></tr>
  <tr><th>Gross Salary</th><th>₹${Number(slip.grossSalary||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
      <th>Total Deductions</th><th>₹${(Number(slip.lopDeduction||0)+Number(slip.pfDeduction||0)+Number(slip.esiDeduction||0)+Number(slip.professionalTax||0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th></tr>
  </tbody></table>
  <table style="margin-top:10px"><tr class="net">
  <td colspan="3" align="right" style="font-size:15px;padding:10px;background:#dcfce7;border:2px solid #16a34a">
  NET SALARY PAYABLE: ₹${Number(slip.netSalary||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr></table>
  <p style="margin-top:20px;font-size:11px;color:#9ca3af">Working Days: ${slip.workingDays} | Present: ${slip.presentDays} | Leave: ${slip.leaveDays} | LOP: ${slip.lopDays}</p>
  <p style="font-size:11px;color:#9ca3af">This is a computer-generated salary slip.</p>
  </body></html>`;
}
